"use client"

import React from "react"
import { LucideIcon } from "lucide-react"
import { motion } from "framer-motion"

interface MetricSparkCardProps {
  label: string
  value: string | number
  subValue?: string
  icon: LucideIcon
  colorClass: string // e.g., "text-indigo-500"
  glowColor: string  // e.g., "bg-indigo-500/20"
  index?: number
}

const MetricSparkCard = ({
  label,
  value,
  subValue,
  icon: Icon,
  colorClass,
  glowColor,
  index = 0
}: MetricSparkCardProps) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      className="relative overflow-hidden rounded-2xl border border-white/10 bg-slate-900/40 p-6 backdrop-blur-xl transition-all hover:border-white/20 group"
    >
      {/* Background Glow */}
      <div className={`absolute -right-8 -top-8 h-32 w-32 rounded-full blur-3xl transition-opacity duration-500 group-hover:opacity-40 opacity-20 ${glowColor}`} />

      <div className="flex items-center justify-between mb-4">
        <span className="text-sm font-medium text-slate-400 group-hover:text-slate-300 transition-colors uppercase tracking-wider">{label}</span>
        <div className={`p-2.5 rounded-xl bg-white/5 ${colorClass} border border-white/5`}>
          <Icon className="h-5 w-5" />
        </div>
      </div>

      <div className="flex flex-col gap-1 relative z-10">
        <h3 className="text-3xl font-bold text-white tracking-tight">{value}</h3>
        {subValue && (
          <span className="text-xs font-semibold text-slate-500 uppercase tracking-widest mt-1">
            {subValue}
          </span>
        )}
      </div>

      {/* Mini Sparkline (SVG) */}
      <div className="mt-6 h-12 w-full relative">
        <svg className="h-full w-full" viewBox="0 0 100 40" preserveAspectRatio="none">
          <defs>
            <linearGradient id={`gradient-${label.replace(/\s+/g, '-').toLowerCase()}`} x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="currentColor" stopOpacity="0.4" className={colorClass} />
              <stop offset="100%" stopColor="currentColor" stopOpacity="0" className={colorClass} />
            </linearGradient>
          </defs>
          <motion.path
            initial={{ pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={{ duration: 1.5, delay: index * 0.2 }}
            d="M0 35 L 10 32 L 20 38 L 30 25 L 40 30 L 50 15 L 60 22 L 70 8 L 80 18 L 90 12 L 100 20 V 40 H 0 Z"
            fill={`url(#gradient-${label.replace(/\s+/g, '-').toLowerCase()})`}
          />
          <motion.path
            initial={{ pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={{ duration: 1.5, delay: index * 0.2 }}
            d="M0 35 L 10 32 L 20 38 L 30 25 L 40 30 L 50 15 L 60 22 L 70 8 L 80 18 L 90 12 L 100 20"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            className={colorClass}
          />
        </svg>
      </div>
    </motion.div>
  )
}

export default MetricSparkCard
